import type { Discount } from '@carvana/merch-contracts';
import {
  type ModalQueueGeneratedOpener,
  useModalQueue
} from '@carvana/merch-react-hooks';
import { MoreDetailsModalSections } from '@carvana/merch-tile';
import deepmerge from 'deepmerge';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useMemo,
  useState
} from 'react';
import { useIsMobile } from '~/context/common/DeviceContext';
import type { AppliedFilters } from '~/contracts/common';
import type { DeepPartial } from '~/types';
import { ModalId, ModalPriorities } from './constants';
import type { MoreDetailsOptions } from './types';

export type AppraisalEmbeddedModalOpenSource = 'home-card' | 'home-carousel';

type ContextType = {
  appraisalEditAcquisitionName: string | undefined;
  appraisalEmbeddedModalOpenSource:
    | AppraisalEmbeddedModalOpenSource
    | undefined;
  filtersToSave: AppliedFilters;
  geicoDiscount: Discount | null;
  isAddCoApplicantModalOpen: boolean;
  isAnyModalOpen: boolean;
  isAppraisalEmbeddedModalOpen: boolean;
  isAppraisalRefreshModalOpen: boolean;
  isComposerUiModalOpen: boolean;
  isGeicoDiscountModalOpen: boolean;
  isMoreDetailsModalOpen: boolean;
  isSampleTermsModalOpen: boolean;
  isSaveSearchModalOpen: boolean;
  isSearchBarFocused: boolean;
  isSearchBarModalOpen: boolean;
  isShippingModalOpen: boolean;
  isSoftPullModalOpen: boolean;
  moreDetailsOptions: MoreDetailsOptions;
  setAddCoApplicantModalOpen: ModalQueueGeneratedOpener;
  setAppraisalEditAcquisitionName: Dispatch<SetStateAction<string | undefined>>;
  setAppraisalEmbeddedModalOpen: (
    isOpen: boolean,
    source?: AppraisalEmbeddedModalOpenSource
  ) => void;
  setAppraisalRefreshModalOpen: ModalQueueGeneratedOpener;
  setComposerUiModalOpen: ModalQueueGeneratedOpener;
  setGeicoDiscountModalOpen: (isOpen: boolean, discount?: Discount) => void;
  setIsSearchBarFocused: Dispatch<SetStateAction<boolean>>;
  setSaveSearchModalOpen: (isOpen: boolean, filters: AppliedFilters) => void;
  setSearchBarModalOpen: ModalQueueGeneratedOpener;
  setSoftPullModalOpen: ModalQueueGeneratedOpener;
  setSampleTermsModalOpen: ModalQueueGeneratedOpener;
  setShippingModalOpen: ModalQueueGeneratedOpener;
  setMoreDetailsModalOpen: (
    isOpen: boolean,
    options?: MoreDetailsOptions
  ) => void;
};

export const defaultContext: ContextType = {
  appraisalEditAcquisitionName: undefined,
  appraisalEmbeddedModalOpenSource: undefined,
  filtersToSave: {},
  geicoDiscount: null,
  isAddCoApplicantModalOpen: false,
  isAnyModalOpen: false,
  isAppraisalEmbeddedModalOpen: false,
  isAppraisalRefreshModalOpen: false,
  isComposerUiModalOpen: false,
  isGeicoDiscountModalOpen: false,
  isMoreDetailsModalOpen: false,
  isSampleTermsModalOpen: false,
  isSaveSearchModalOpen: false,
  isSearchBarFocused: false,
  isSearchBarModalOpen: false,
  isShippingModalOpen: false,
  isSoftPullModalOpen: false,
  moreDetailsOptions: {
    openTo: MoreDetailsModalSections.PriceDetails
  },
  setAddCoApplicantModalOpen() {},
  setAppraisalEditAcquisitionName() {},
  setAppraisalEmbeddedModalOpen() {},
  setAppraisalRefreshModalOpen() {},
  setComposerUiModalOpen() {},
  setGeicoDiscountModalOpen() {},
  setIsSearchBarFocused() {},
  setSaveSearchModalOpen() {},
  setSearchBarModalOpen() {},
  setSoftPullModalOpen() {},
  setSampleTermsModalOpen() {},
  setShippingModalOpen() {},
  setMoreDetailsModalOpen() {}
};

const ModalContext = createContext<ContextType>(defaultContext);

export function ModalContextProvider({ children }: React.PropsWithChildren) {
  const [currentModalId, generateOpener, modalQueue] =
    useModalQueue(ModalPriorities);
  const isMobile = useIsMobile();
  const [appraisalEditAcquisitionName, setAppraisalEditAcquisitionName] =
    useState<string | undefined>(defaultContext.appraisalEditAcquisitionName);
  const [
    appraisalEmbeddedModalOpenSource,
    setAppraisalEmbeddedModalOpenSource
  ] = useState<AppraisalEmbeddedModalOpenSource | undefined>(
    defaultContext.appraisalEmbeddedModalOpenSource
  );
  const [filtersToSave, setFiltersToSave] = useState<AppliedFilters>({});
  const [isSearchBarFocused, setIsSearchBarFocused] = useState(false);
  const [geicoDiscount, setGeicoDiscount] = useState<Discount | null>(
    defaultContext.geicoDiscount
  );
  const [moreDetailsOptions, setMoreDetailsOptions] =
    useState<MoreDetailsOptions>(defaultContext.moreDetailsOptions);

  const {
    setAddCoApplicantModalOpen,
    setAppraisalEmbeddedModalOpen,
    setAppraisalRefreshModalOpen,
    setComposerUiModalOpen,
    setGeicoDiscountModalOpen,
    setSaveSearchModalOpen,
    setSearchBarModalOpen,
    setSoftPullModalOpen,
    setSampleTermsModalOpen,
    setShippingModalOpen
  } = useMemo(() => {
    return {
      setAddCoApplicantModalOpen: generateOpener(ModalId.AddCoApplicant),
      setAppraisalEmbeddedModalOpen: generateOpener(ModalId.AppraisalEmbedded),
      setComposerUiModalOpen: generateOpener(ModalId.ComposerUi),
      setGeicoDiscountModalOpen: generateOpener(ModalId.GeicoDiscount),
      setSaveSearchModalOpen: generateOpener(ModalId.SaveSearch),
      setSearchBarModalOpen: generateOpener(ModalId.SearchBar),
      setSoftPullModalOpen: generateOpener(ModalId.SoftPull),
      setAppraisalRefreshModalOpen: generateOpener(ModalId.AppraisalRefresh),
      setSampleTermsModalOpen: generateOpener(ModalId.SampleTerms),
      setShippingModalOpen: generateOpener(ModalId.Shipping)
    };
  }, [generateOpener]);

  const setAppraisalEmbeddedModalOpenWithSource = useCallback<
    ContextType['setAppraisalEmbeddedModalOpen']
  >(
    (isOpen, source) => {
      if (isOpen) setAppraisalEmbeddedModalOpenSource(source);
      setAppraisalEmbeddedModalOpen(isOpen);
    },
    [setAppraisalEmbeddedModalOpen]
  );

  const setSavedSearchModalOpenWithFiltersToSave = useCallback(
    (isOpen: boolean, filters: AppliedFilters) => {
      setSaveSearchModalOpen(isOpen);
      setFiltersToSave(filters);
    },
    [setSaveSearchModalOpen]
  );

  const setGeicoDiscountModalOpenWithDiscount = useCallback<
    ContextType['setGeicoDiscountModalOpen']
  >(
    (isOpen: boolean, discount?: Discount) => {
      if (discount) setGeicoDiscount(discount);
      if (!isOpen) setGeicoDiscount(null);
      setGeicoDiscountModalOpen(isOpen);
    },
    [setGeicoDiscountModalOpen]
  );

  const setMoreDetailsModalOpenWithOptions = useCallback(
    (isOpen: boolean, options?: MoreDetailsOptions) => {
      generateOpener(ModalId.MoreDetails)(isOpen);
      if (options) {
        setMoreDetailsOptions(options);
      }
    },
    [generateOpener]
  );

  const value = useMemo<ContextType>((): ContextType => {
    return {
      appraisalEditAcquisitionName,
      appraisalEmbeddedModalOpenSource,
      filtersToSave,
      geicoDiscount,
      isAddCoApplicantModalOpen: currentModalId === ModalId.AddCoApplicant,
      isAnyModalOpen: currentModalId > -1,
      isAppraisalEmbeddedModalOpen:
        currentModalId === ModalId.AppraisalEmbedded,
      isAppraisalRefreshModalOpen: currentModalId === ModalId.AppraisalRefresh,
      isComposerUiModalOpen: currentModalId === ModalId.ComposerUi,
      isGeicoDiscountModalOpen: currentModalId === ModalId.GeicoDiscount,
      isSaveSearchModalOpen: currentModalId === ModalId.SaveSearch,
      isSearchBarFocused,
      isSearchBarModalOpen: currentModalId === ModalId.SearchBar,
      isSoftPullModalOpen: currentModalId === ModalId.SoftPull,
      isMoreDetailsModalOpen: isMobile
        ? modalQueue.has(ModalId.MoreDetails)
        : currentModalId === ModalId.MoreDetails,
      isSampleTermsModalOpen: currentModalId === ModalId.SampleTerms,
      isShippingModalOpen: currentModalId === ModalId.Shipping,
      moreDetailsOptions: moreDetailsOptions,
      setAddCoApplicantModalOpen,
      setAppraisalEditAcquisitionName,
      setAppraisalEmbeddedModalOpen: setAppraisalEmbeddedModalOpenWithSource,
      setAppraisalRefreshModalOpen,
      setComposerUiModalOpen,
      setGeicoDiscountModalOpen: setGeicoDiscountModalOpenWithDiscount,
      setIsSearchBarFocused,
      setSaveSearchModalOpen: setSavedSearchModalOpenWithFiltersToSave,
      setSearchBarModalOpen,
      setSoftPullModalOpen,
      setSampleTermsModalOpen,
      setShippingModalOpen,
      setMoreDetailsModalOpen: setMoreDetailsModalOpenWithOptions
    };
  }, [
    appraisalEditAcquisitionName,
    appraisalEmbeddedModalOpenSource,
    currentModalId,
    filtersToSave,
    geicoDiscount,
    isMobile,
    isSearchBarFocused,
    modalQueue,
    moreDetailsOptions,
    setAddCoApplicantModalOpen,
    setAppraisalEmbeddedModalOpenWithSource,
    setAppraisalRefreshModalOpen,
    setComposerUiModalOpen,
    setGeicoDiscountModalOpenWithDiscount,
    setSavedSearchModalOpenWithFiltersToSave,
    setMoreDetailsModalOpenWithOptions,
    setSampleTermsModalOpen,
    setSearchBarModalOpen,
    setShippingModalOpen,
    setSoftPullModalOpen
  ]);

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}

ModalContextProvider.displayName = 'ModalContextProvider';

export const generateModalContext = (
  customState: DeepPartial<ContextType>
): ContextType =>
  deepmerge<ContextType, DeepPartial<ContextType>>(defaultContext, customState);

export function MockedModalContextProvider({
  children,
  mockValue
}: React.PropsWithChildren<{ mockValue: DeepPartial<ContextType> }>) {
  return (
    <ModalContext.Provider value={generateModalContext(mockValue)}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  return React.useContext(ModalContext);
}

export default ModalContext;
